import { useEffect, useState } from 'react';
import { MAX_POST_BODY, RefusedError, SocialAuthError, postVerbs } from '@versesverse/core';
import type { PostView } from '@versesverse/core';
import { useLang } from '../i18n/lang';
import { refusalSays } from '../i18n/refusal';
import { useOneAtATime } from './useOneAtATime';

/** What the discussion reads off the social client — the SocialClient is assignable. */
export interface DiscussionClient {
  /** Newest first, and everything older than a cursor — see `earlier` below. */
  listPosts(nodeId: string, options?: { before?: string; beforeId?: string }): Promise<PostView[]>;
  addPost(nodeId: string, body: string): Promise<PostView>;
  /** Correct your own words, within the window the server allows. */
  editPost(postId: string, body: string): Promise<PostView>;
  /** Take back your own words. A moderator may also remove somebody else's. */
  deletePost(postId: string): Promise<void>;
  /** Say that something is not all right — §9's moderation floor, which had no control. */
  reportPost(postId: string): Promise<void>;
  signInUrl(): string;
}

export interface DiscussionProps {
  client: DiscussionClient;
  nodeId: string;
  /**
   * Our own id, so a reader can tell which words are theirs.
   *
   * The API has always returned `authorId` and this list rendered the body alone, so every post
   * read as anonymous — you could not see which were yours, which is what makes "remove" possible
   * at all. Null when signed out, and then no verb is offered, because all of them need an account.
   */
  meId: string | null;
}

/*
 * A NODE'S DISCUSSION, in the panel. Anyone reads it; posting needs a signed-in reader, and a
 * write that comes back 401 turns into a Google sign-in offer rather than a silent failure — the
 * reading-anonymous, participate-signed-in rule (D11), made visible.
 */
export function Discussion({ client, nodeId, meId }: DiscussionProps): React.JSX.Element {
  const { t } = useLang();
  const [posts, setPosts] = useState<PostView[] | null>(null);
  const [unread, setUnread] = useState(false);
  /** Whether the last page back came up empty — the start of the discussion, so the control goes. */
  const [ended, setEnded] = useState(false);
  const [draft, setDraft] = useState('');
  const [needsSignIn, setNeedsSignIn] = useState(false);
  /**
   * WHAT WENT WRONG, in the server's words where it wrote any.
   *
   * This was a boolean, so every explained failure in this panel collapsed into one generic line:
   * "no such post of yours to edit", a rate limit's own sentence, a validation complaint — the
   * reader got the same words for all of them. The phone shows what the server said; the web was
   * the weaker of the two clients, which is the third time that has been true this week.
   *
   * `true` keeps the generic line for a failure that carried no sentence at all, so the string
   * case can only ever be something the server chose to write.
   */
  const [postFailed, setPostFailed] = useState<string | true | null>(null);
  /** Whether the last "earlier posts" read failed — never the same as the discussion starting here. */
  const [earlierFailed, setEarlierFailed] = useState(false);
  /** Posts this reader has already reported, so the control says so rather than inviting a second. */
  const [reported, setReported] = useState<string[]>([]);
  /** Which post is open for correction, and the words as they are being fixed. */
  const [editing, setEditing] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  /*
   * ONE REMOVE PER POST. `DELETE posts/:id` answers `no such post to remove` once the post is
   * gone, so a second press earns a refusal BECAUSE THE FIRST WORKED — and `act` below says every
   * refusal out loud. The row is on screen for the whole round trip, which is the window.
   *
   * Reporting needs no guard and deliberately has none: the server's own note says one reader,
   * one report, *"the count is the SIZE OF A SET rather than a tally of presses"*, so a second
   * press answers the same thing the first did.
   */
  const row = useOneAtATime();
  /** Every verb on a post fails the same two ways, so they are handled once. */
  /** Take a post down — one removal per post, and `act` turns any failure into a sentence. */
  const remove = async (postId: string): Promise<void> => row.run(postId, () => act(async () => {
    await client.deletePost(postId);
    setPosts(current => (current ?? []).filter(p => p.id !== postId));
  }));

  const act = async (action: () => Promise<void>): Promise<void> => {
    setNeedsSignIn(false);
    setPostFailed(null);
    try {
      await action();
    } catch (caught) {
      if (caught instanceof SocialAuthError) setNeedsSignIn(true);
      /* Only what the server deliberately wrote — "Failed to fetch" is written for whoever wrote
         the request, and everything without a sentence falls to the generic line below. */
      else if (caught instanceof RefusedError) setPostFailed(refusalSays(t, caught));
      else setPostFailed(true);
    }
  };

  useEffect(() => {
    let live = true;
    setPosts(null);
    setEnded(false);
    setNeedsSignIn(false);
    client.listPosts(nodeId)
      .then(list => { if (live) { setPosts(list); setUnread(false); } })
      /*
       * NOT AN EMPTY DISCUSSION. "No one has written here yet" is the most on-brand instance of
       * this defect in the product: the plane exists to show where traditions disagree, and a
       * failed read told a reader there was no disagreement to see.
       */
      .catch(() => { if (live) { setPosts([]); setUnread(true); } });
    return () => { live = false; };
  }, [client, nodeId]);

  /**
   * EVERYTHING BEFORE WHAT IS ON SCREEN — the older half of the argument, which nothing could reach.
   *
   * The server has taken a `before` cursor since the discussion was built and no client sent one,
   * so every reader saw the newest fifty of a node's posts and **the fifty-first did not exist**:
   * not for a stranger, not for the person who wrote it, not for a moderator judging a report about
   * it. On the plane whose entire subject is where traditions disagree, that is the disagreement
   * being trimmed by an implementation detail.
   *
   * The cursor is the OLDEST post already held, so "earlier" means exactly what it says. A page
   * that comes back empty is the start of the discussion and the control goes away — a button that
   * does nothing is worse than no button. The page SIZE is never named here: it is the server's,
   * and a copy of it in this file would be a second one to keep in step.
   */
  const earlier = async (): Promise<void> => {
    setEarlierFailed(false);
    const oldest = posts?.[posts.length - 1];
    if (!oldest) return;
    try {
      /* The ROW it stopped at, not just the moment: two posts can share a millisecond — which
         is ordinary here — and a page boundary between them lost the second for ever. */
      const older = await client.listPosts(nodeId, { before: oldest.createdAt, beforeId: oldest.id });
      setPosts(current => [...(current ?? []), ...older]);
    /*
     * INSIDE `act`, so a page that FAILED is not read as the start of the discussion.
     *
     * This read throws — the discussion is the one list that always did — so the failure was an
     * unhandled rejection and the reader got silence from a press, which this panel calls the one
     * thing that must not happen. And had it answered empty instead, `ended` would have taken the
     * control away and told them the conversation starts here. The same defect stood in both
     * moderation records, in the function beside the one it had already been fixed in.
     *
     * Reached only when the read SUCCEEDED, so an empty page still means the start.
     */
      setEnded(older.length === 0);
    } catch {
      /*
       * ITS OWN SENTENCE, not `act`'s. Running this through the panel's verb handler was the
       * first fix and it said *"Could not post — please try again"* to somebody who had pressed
       * "Earlier posts": visible, and false in every word. A message about the wrong act is the
       * shape §0 already records about a blocked microphone.
       */
      setEarlierFailed(true);
    }
  };

  /*
   * Through `act`, not beside it. This had its own copy of the same try/catch — and the copy was
   * the one that stayed a boolean, so POSTING, the commonest write in the panel, was the last path
   * still throwing away what the server said. Two handlers meant one of them would be forgotten,
   * and it was.
   */
  const submit = async (): Promise<void> => {
    const body = draft.trim();
    if (!body) return;
    await act(async () => {
      const created = await client.addPost(nodeId, body);
      setPosts(current => [created, ...(current ?? [])]);
      setDraft('');
    });
  };

  return (
    <section className="discussion" aria-label={t('discussion.label')}>
      <form onSubmit={event => { event.preventDefault(); void submit(); }}>
        <label className="sr-only" htmlFor="discussion-draft">{t('discussion.add')}</label>
        <textarea
          id="discussion-draft"
          className="discussion-draft"
          value={draft}
          maxLength={MAX_POST_BODY}
          rows={3}
          placeholder={t('discussion.placeholder')}
          onChange={event => setDraft(event.target.value)}
        />
        <button type="submit" className="discussion-post" disabled={!draft.trim()}>{t('discussion.post')}</button>
      </form>

      {needsSignIn && (
        <p className="discussion-signin" role="alert">
          <a href={client.signInUrl()}>{t('auth.google')}</a>{t('discussion.toTakePart')}
        </p>
      )}
      {postFailed && (
        <p className="discussion-error" role="alert">
          {typeof postFailed === 'string' ? postFailed : t('discussion.postError')}
        </p>
      )}

      {posts === null ? (
        <p className="discussion-loading" role="status" aria-live="polite">{t('discussion.loading')}</p>
      ) : posts.length === 0 ? (
        <p className="discussion-empty" role={unread ? 'alert' : undefined}>
          {t(unread ? 'shared.couldNotRead' : 'discussion.empty')}
        </p>
      ) : (
        <ol className="discussion-list">
          {posts.map(post => {
            /* core's rule, shared with the phone: `postVerbs` is where the reason signed-out gets
               NOTHING rather than "report only" is written down, and it is what stopped this
               branching being written a second time when the phone grew the same three controls. */
            const verbs = postVerbs(post, meId);
            return (
              <li key={post.id} className="discussion-item">
                {/*
                  EDITING IN PLACE, because a correction belongs where the words are. The row turns
                  into the words themselves rather than opening a form somewhere else — you are
                  fixing a sentence, not filling anything in.
                */}
                {editing === post.id ? (
                  <form
                    className="discussion-edit"
                    onSubmit={event => {
                      event.preventDefault();
                      void act(async () => {
                        const saved = await client.editPost(post.id, editDraft.trim());
                        setPosts(current => (current ?? []).map(p => (p.id === saved.id ? saved : p)));
                        setEditing(null);
                      });
                    }}
                  >
                    <label className="sr-only" htmlFor={`edit-${post.id}`}>{t('discussion.editing')}</label>
                    <textarea
                      id={`edit-${post.id}`}
                      value={editDraft}
                      maxLength={MAX_POST_BODY}
                      rows={3}
                      onChange={event => setEditDraft(event.target.value)}
                    />
                    <button type="submit" disabled={!editDraft.trim()}>{t('discussion.save')}</button>
                    <button type="button" onClick={() => setEditing(null)}>{t('discussion.cancelEdit')}</button>
                  </form>
                ) : (
                  <span className="discussion-body">{post.body}</span>
                )}
                {/* Says a post was changed, so nobody is quietly given different words than they
                    replied to. The server sets it; this only shows it. */}
                {post.editedAt && editing !== post.id && (
                  <span className="discussion-edited">{t('discussion.edited')}</span>
                )}

                {/*
                  THE THREE VERBS A READER HAD NO WAY TO USE.
                  `PATCH /posts/:id`, `DELETE /posts/:id` and `POST /posts/:id/report` have been
                  answered by the server since the discussion was built and called by no client.
                  Reporting is the one that matters most: §9 names it as part of the moderation
                  floor this launches with, and in a product whose subject is where traditions
                  disagree, having no way to say "this is not all right" is the gap of the six.

                  Shown only to a signed-in reader, because all three need an account and offering
                  a control that will answer 401 is worse than offering none.
                */}
                {verbs.length > 0 && (
                  <span className="discussion-verbs">
                    {verbs.includes('edit') && editing !== post.id && (
                      <button
                        type="button"
                        className="discussion-edit-start"
                        onClick={() => { setEditing(post.id); setEditDraft(post.body); }}
                      >
                        {t('discussion.edit')}
                      </button>
                    )}
                    {verbs.includes('remove') ? (
                      <button
                        type="button"
                        className="discussion-remove"
                        onClick={() => void remove(post.id)}
                        disabled={row.working(post.id)}
                      >
                        {t('discussion.remove')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="discussion-report"
                        onClick={() => void act(async () => {
                          await client.reportPost(post.id);
                          setReported(current => [...current, post.id]);
                        })}
                        disabled={reported.includes(post.id)}
                      >
                        {reported.includes(post.id) ? t('discussion.reported') : t('discussion.report')}
                      </button>
                    )}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {/*
        THE OLDER HALF OF THE ARGUMENT — see `earlier`.

        Offered whenever there is anything on screen and the last page back was not empty. It does
        not try to guess whether more exists by counting against a page size: that number is the
        server's, and a copy of it here would be a second one to keep in step. A node with nine
        posts costs one click to learn there are no more, and then the control goes for good.
      */}
      {earlierFailed && (
        <p className="discussion-earlier-failed" role="alert">{t('discussion.earlierFailed')}</p>
      )}
      {posts !== null && posts.length > 0 && !ended && (
        <button type="button" className="discussion-earlier" onClick={() => void earlier()}>
          {t('discussion.earlier')}
        </button>
      )}
    </section>
  );
}
